if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        // Handle variant-required state based on URL parameter
        this.handleVariantRequiredState();
      }

      handleVariantRequiredState() {
        // Check if button has data-variant-required attribute
        if (this.submitButton.dataset.variantRequired === 'true') {
          // Check if variant is in URL
          const urlParams = new URLSearchParams(window.location.search);
          const hasVariant = urlParams.has('variant');

          if (hasVariant) {
            // Variant is selected, enable the button
            this.enableSubmitButton();
          } else {
            // No variant selected, keep disabled
            this.disableSubmitButton();
          }

          // Listen for URL changes
          this.setupVariantUrlListener();
        }
      }

      setupVariantUrlListener() {
        const checkVariantInUrl = () => {
          const urlParams = new URLSearchParams(window.location.search);
          const hasVariant = urlParams.has('variant');

          if (hasVariant) {
            this.enableSubmitButton();
          } else {
            this.disableSubmitButton();
          }
        };

        // Listen for popstate (browser back/forward)
        window.addEventListener('popstate', checkVariantInUrl);

        // Subscribe to pub/sub variant changes if available
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            // Small delay to ensure URL is updated
            setTimeout(checkVariantInUrl, 50);
          });
        }
      }

      disableSubmitButton() {
        this.submitButton.setAttribute('disabled', 'disabled');
        this.variantIdInput.disabled = true;
        if (this.submitButtonText) {
          this.submitButtonText.textContent = window.variantStrings?.selectVariant || 'Select a variant';
        }
      }

      enableSubmitButton() {
        this.submitButton.removeAttribute('disabled');
        this.variantIdInput.disabled = false;
        if (this.submitButtonText && window.variantStrings?.addToCart) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (window.FlyToCart) {
                      FlyToCart.animate({
                        triggerElement: this.submitButton,
                        onComplete: () => {
                          this.cart.renderContents(response);
                        },
                      });
                    } else {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              if (window.FlyToCart) {
                FlyToCart.animate({
                  triggerElement: this.submitButton,
                  onComplete: () => {
                    this.cart.renderContents(response);
                  },
                });
              } else {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        // Hide Buy Now only when specifically sold out
        const buyItNowHandler = this.querySelector('buy-it-now-handler');
        if (buyItNowHandler) {
          const isSoldOut = disable && (text === window.variantStrings.soldOut || text === window.variantStrings.unavailable);
          if (isSoldOut) {
            buyItNowHandler.dataset.soldout = 'true';
          } else if (!disable) {
            delete buyItNowHandler.dataset.soldout;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
